import React, { useEffect, useRef, useState } from "react";
import { Alert, CircularProgress, Slider, Typography } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";

function formatUsd(amount) {
  return `$${Number(amount || 0).toFixed(2)}`;
}

const ESTIMATE_DEBOUNCE_MS = 300;
const REQUEST_TIMEOUT_MS = 45000;
const DEFAULT_SLIDER_TARGET = 100;

function defaultPinLimit(min, max, saved) {
  if (typeof saved === "number" && saved > 0) {
    return Math.min(saved, max);
  }
  // First-time default: 100 pins, or all if agency has fewer than 100 mappable.
  return Math.min(DEFAULT_SLIDER_TARGET, max);
}

function clampPinLimit(value, min, max) {
  if (value == null || max <= 0) return min;
  return Math.max(min, Math.min(value, max));
}

function withTimeout(promise, ms = REQUEST_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Request timed out")), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

const MapPinLimitSection = ({
  agencyId,
  showMap,
  showMapPins,
  onLimitSaved,
}) => {
  const services = useAppServices();
  const servicesRef = useRef(services);
  servicesRef.current = services;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [pinLimit, setPinLimit] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState(null);

  const estimateTimerRef = useRef(null);
  const estimateRequestIdRef = useRef(0);

  const fetchEstimate = async (limit, requestId) => {
    if (!agencyId || limit == null) return;

    setEstimateLoading(true);
    setEstimateError(null);

    try {
      const { response, error } = await withTimeout(
        servicesRef.current.accounts.mapPinEstimate({
          query: `agency_id=${agencyId}&limit=${limit}`,
        })
      );

      if (requestId !== estimateRequestIdRef.current) return;

      if (error || !response?.success) {
        throw new Error(error?.message || response?.message || "Failed to calculate estimate");
      }

      setEstimate(response.data);
    } catch (err) {
      if (requestId !== estimateRequestIdRef.current) return;
      setEstimate(null);
      setEstimateError(err?.message || "Failed to calculate estimate");
    } finally {
      if (requestId === estimateRequestIdRef.current) {
        setEstimateLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!showMap || !showMapPins || !agencyId) return undefined;

    let cancelled = false;

    async function init() {
      setLoading(true);
      setStatsError(null);
      setEstimateError(null);

      try {
        const { response, error } = await withTimeout(
          servicesRef.current.accounts.mapPinStats({
            query: `agency_id=${agencyId}`,
          })
        );

        if (cancelled) return;

        if (error || !response?.success) {
          throw new Error(error?.message || response?.message || "Failed to load map pin stats");
        }

        const data = response.data;
        setStats(data);

        const min = data.sliderMin || 1;
        const max = data.sliderMax || 0;
        if (max <= 0) return;

        const initial = defaultPinLimit(min, max, data.currentMapPinLimit);

        setPinLimit(initial);

        const requestId = ++estimateRequestIdRef.current;
        await fetchEstimate(initial, requestId);
      } catch (err) {
        if (!cancelled) {
          setStatsError(err?.message || "Failed to load map pin stats");
          setStats(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [agencyId, showMap, showMapPins]);

  useEffect(() => {
    return () => {
      if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!stats?.sliderMax) return;
    const min = stats.sliderMin || 1;
    const max = stats.sliderMax;
    setPinLimit((prev) => clampPinLimit(prev, min, max));
  }, [stats?.sliderMin, stats?.sliderMax]);

  const scheduleEstimate = (limit) => {
    if (estimateTimerRef.current) clearTimeout(estimateTimerRef.current);
    estimateTimerRef.current = setTimeout(() => {
      const requestId = ++estimateRequestIdRef.current;
      fetchEstimate(limit, requestId);
    }, ESTIMATE_DEBOUNCE_MS);
  };

  const handleSliderChange = (_, value) => {
    setPinLimit(value);
    scheduleEstimate(value);
  };

  const handleSave = async () => {
    if (!agencyId || pinLimit == null || !stats) return;

    const limitToSave = clampPinLimit(
      pinLimit,
      stats.sliderMin || 1,
      stats.sliderMax || 0,
    );
    if (limitToSave <= 0) return;

    if (estimate?.newGeocodes > 0 && !stats.canGeocode) {
      toast.error("google_image_api_key is required to geocode new map pins.");
      return;
    }

    setSaving(true);
    try {
      const { response, error } = await withTimeout(
        servicesRef.current.accounts.saveMapPinLimit({
          payload: {
            agency_id: agencyId,
            limit: limitToSave,
            dry_run: false,
          },
        }),
        120000
      );

      if (error || !response?.success) {
        throw new Error(error?.message || response?.message || "Failed to save map pin limit");
      }

      const geocoded = response.data?.geocoded;
      const okCount = geocoded?.ok ?? 0;
      const failedCount = geocoded?.failed ?? 0;

      toast.success(
        failedCount > 0
          ? `Map pin limit saved. Geocoded ${okCount}, failed ${failedCount}.`
          : "Map pin limit saved."
      );

      const statsRes = await servicesRef.current.accounts.mapPinStats({
        query: `agency_id=${agencyId}`,
      });
      if (statsRes.response?.success) {
        const freshStats = statsRes.response.data;
        setStats(freshStats);
        const freshMax = freshStats.sliderMax || 0;
        const freshMin = freshStats.sliderMin || 1;
        if (freshMax > 0) {
          setPinLimit((prev) => clampPinLimit(prev ?? pinLimit, freshMin, freshMax));
        }
      }

      const requestId = ++estimateRequestIdRef.current;
      await fetchEstimate(limitToSave, requestId);

      if (onLimitSaved) {
        await onLimitSaved(limitToSave);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to save map pin limit");
    } finally {
      setSaving(false);
    }
  };

  if (!showMap || !showMapPins) return null;

  if (loading && !stats) {
    return (
      <SoftBox display="flex" alignItems="center" gap={1} mt={1} mb={2}>
        <CircularProgress size={18} />
        <SoftTypography variant="caption">Loading map pin settings…</SoftTypography>
      </SoftBox>
    );
  }

  if (statsError) {
    return (
      <SoftBox mt={1} mb={2}>
        <Alert severity="error" sx={{ fontSize: "13px" }}>
          {statsError}
        </Alert>
      </SoftBox>
    );
  }

  if (!stats || stats.sliderMax <= 0) {
    return (
      <SoftBox mt={1} mb={2}>
        <Alert severity="info" sx={{ fontSize: "13px" }}>
          No listings with an address yet — map pins cannot be configured until addresses exist.
        </Alert>
      </SoftBox>
    );
  }

  const sliderMin = stats.sliderMin || 1;
  const sliderMax = stats.sliderMax;
  const hasSavedLimit = stats.currentMapPinLimit != null;
  const effectiveLimit = clampPinLimit(pinLimit, sliderMin, sliderMax);
  const savedPinCount = hasSavedLimit
    ? clampPinLimit(stats.currentMapPinLimit, sliderMin, sliderMax)
    : null;
  const newGeocodes = estimate?.newGeocodes ?? 0;
  const estimatedUsd = estimate?.estimatedUsd ?? 0;

  return (
    <SoftBox mt={2} mb={2} sx={{ pl: 0.5 }}>
      <SoftTypography fontWeight="bold" mb={0.5} sx={{ fontSize: "14px" }}>
        Homepage map pins
      </SoftTypography>
      <SoftTypography variant="caption" sx={{ color: "#666", display: "block", mb: 1.5 }}>
        {hasSavedLimit
          ? `Your homepage map is set to show ${savedPinCount} pin${savedPinCount === 1 ? "" : "s"}. Move the slider and save to change how many businesses appear on the map.`
          : "Choose how many business pins to show on the homepage. Saving geocodes each listing once on your Google account instead of on every visitor."}
      </SoftTypography>

      {!stats.canGeocode && stats.needsGeocode > 0 && (
        <Alert severity="warning" sx={{ fontSize: "12px", mb: 1.5 }}>
          Add a server-side Google API key (google_image_api_key) before saving new pins.
        </Alert>
      )}

      <Typography sx={{ mb: 0.25, fontSize: "13px" }}>
        Show <strong>{effectiveLimit}</strong> businesses on the homepage map
      </Typography>
      <SoftTypography
        variant="caption"
        sx={{ color: "#666", display: "block", mb: 1, fontSize: "12px" }}
      >
        {sliderMax} {sliderMax === 1 ? "listing has" : "listings have"} an address
      </SoftTypography>

      <Slider
        value={effectiveLimit}
        min={sliderMin}
        max={sliderMax}
        step={1}
        onChange={handleSliderChange}
        valueLabelDisplay="auto"
        disabled={saving}
        sx={{ maxWidth: 480, mb: 1.5 }}
      />

      <SoftBox
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: "#f5f7fa",
          border: "1px solid #e8ecf0",
          maxWidth: 480,
          mb: 1.5,
        }}
      >
        {estimateLoading ? (
          <SoftBox display="flex" alignItems="center" gap={1}>
            <CircularProgress size={14} />
            <SoftTypography variant="caption">Calculating estimate…</SoftTypography>
          </SoftBox>
        ) : estimateError ? (
          <SoftTypography variant="caption" sx={{ color: "#d32f2f" }}>
            {estimateError}
          </SoftTypography>
        ) : estimate ? (
          <>
            <SoftTypography variant="body2" sx={{ fontSize: "13px" }}>
              {newGeocodes === 0
                ? "No additional geocoding needed for this limit."
                : `${newGeocodes} new geocode${newGeocodes === 1 ? "" : "s"} on save`}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ fontSize: "13px", fontWeight: "bold", mt: 0.5 }}>
              Estimated one-time cost: {formatUsd(estimatedUsd)}
            </SoftTypography>
            {newGeocodes > 0 && (
              <SoftTypography
                variant="caption"
                sx={{ color: "#666", display: "block", mt: 0.75, fontSize: "12px" }}
              >
                This is a one-time charge per pin added to the map.
              </SoftTypography>
            )}
          </>
        ) : (
          <SoftTypography variant="caption" sx={{ color: "#666" }}>
            Estimate unavailable — check backend is running on os-882.
          </SoftTypography>
        )}
      </SoftBox>

      <SoftButton
        variant="gradient"
        color="dark"
        size="small"
        onClick={handleSave}
        disabled={saving || estimateLoading}
      >
        {saving ? "Saving map pins…" : "Save map pin limit"}
      </SoftButton>
    </SoftBox>
  );
};

export default MapPinLimitSection;
